import React, { useState } from 'react';

// Utils
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { useMutation } from 'react-query';
import withAuth from '../../../utils/hocs/withAuth';
import withSidebar from '../../../utils/hocs/withSidebar';
import SCOPE from '../../../utils/gate/scope';
import withPermission from '../../../utils/hocs/withPermission';
import useNotification from '../../../utils/hooks/useNotification';
import buildTabs from '../../../utils/tools/buildTabs';
import extraCreationSchema from '../../../validations/products/extra/creationSchema';
import useForm from '../../../utils/hooks/useForm';

// Services
import useExtraService from '../../../services/extraService';

// Icons
import { ReactComponent as FileIcon } from '../../../assets/icons/file.svg';
import { ReactComponent as PictureIcon } from '../../../assets/icons/picture.svg';

// Components
import PageWrapper from '../../../components/layout/PageWrapper/PageWrapper';
import General from '../../../components/views/establishment/Products/Extras/Sections/General/General';
import LayoutWithTabs from '../../../components/layout/LayoutWithTabs/LayoutWithTabs';
import Medias from '../../../components/views/establishment/Products/Extras/Sections/Medias/Medias';
import SkeletonLoader from '../../../components/molecules/SkeletonLoader/SkeletonLoader';
import Button from '../../../components/atoms/Button/Button';
import Translations from '../../../components/views/establishment/Products/Common/Translations/Translations';

function ExtraCreation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const { displayNotification } = useNotification();
  const { handleScrollToError } = useForm();

  const [initialValues] = useState({
    name: '',
    type: null,
    mainImageMedia: null,

    // english content
    title: '',
    description: '',
  });

  const { postExtra } = useExtraService();

  const { isLoading: isPostExtraLoading, mutate: postExtraMutate } = useMutation(
    (values) => postExtra(values),
    {
      onSuccess: () => {
        navigate('/products/extras', {
          state: 'extras.creation.form.success',
        });
      },
      onError: (error) => {
        displayNotification({
          message: t(error?.response?.data?.code
            ? `code_errors.${error.response.data.code}`
            : 'extras.creation.form.error'),
          color: 'error',
        });
      },
    },
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (values) {
        postExtraMutate({
          name: values.name,
          type: values.type?.value,
          mainImageMediaId: values.mainImageMedia?.id,

          // english content
          englishTitle: values.title,
          englishDescription: values.description,
        });
      }
    },
    validationSchema: extraCreationSchema,
    validateOnBlur: true,
  });

  const getTabComponent = (TabComponent, props) => (
    <TabComponent
      formik={{
        values: formik.values,
        handleChange: formik.handleChange,
        handleBlur: formik.handleBlur,
        setFieldValue: formik.setFieldValue,
        setFieldTouched: formik.setFieldTouched,
        touched: formik.touched,
        errors: formik.errors,
      }}
      {...props}
    />
  );

  const tabs = buildTabs([
    {
      label: 'extras.marketing.sections.general',
      component: getTabComponent(General, {}),
      icon: <FileIcon />,
    },
    {
      label: 'extras.creation.sections.translations',
      component: getTabComponent(
        Translations,
        { warningMessage: t('extras.creation.englishRequired'), productType: 'extras' },
      ),
      icon: <FileIcon />,
    },
    {
      label: 'extras.marketing.sections.medias',
      component: getTabComponent(Medias, {}),
      icon: <PictureIcon />,
    },
  ]);

  return (
    <PageWrapper>
      <LayoutWithTabs
        tabs={tabs}
        breadcrumb={[
          { label: t('extras.title'), href: '/products/extras' },
        ]}
        title={t('extras.add')}
        skeletonLoader={(
          <>
            <SkeletonLoader className="m-b-30" height={30} />

            {tabs?.map((_, index) => (
              <React.Fragment key={index}>
                <SkeletonLoader className="m-b-20" height={50} />
                <SkeletonLoader className="m-b-50" height={200} />
              </React.Fragment>
            ))}
          </>
        )}
        moreContent={(
          <div className="d-flex a-center j-end sticky-actions p-r-30 mobile-p-r-0">
            <Button
              full={isMobile}
              onClick={() => {
                formik.submitForm().then(() => handleScrollToError(formik.errors));
              }}
              isLoading={isPostExtraLoading}
              disabled={!formik.dirty}
            >
              {t('extras.creation.form.submit')}
            </Button>
          </div>
        )}
      />
    </PageWrapper>
  );
}

export default withAuth(
  withPermission(
    withSidebar(ExtraCreation, { withTabsBar: true }),
    {
      scopes: [SCOPE.create_ownEst_extra],
    },
  ),
);
